import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/eula')({
  component: Eula,
});

function Eula() {
  return (
    <div style={pageStyle}>
      <article style={articleStyle}>
        <header>
          <h1 style={h1Style}>End User License Agreement</h1>
          <p style={metaStyle}>Effective date: February 19, 2026</p>
        </header>

        <p>
          This End User License Agreement ("EULA") is a legal agreement between you ("you" or "Licensee")
          and Interlucent ("we", "us", or "Licensor") governing your use of the Interlucent software,
          including the console application, SDKs, APIs, streaming components, CLI tools, and worker agent
          runtime (collectively, the "Software").
        </p>
        <p>
          By installing, accessing, or using the Software, you agree to be bound by this EULA. If you do
          not agree, do not use the Software.
        </p>
        <p>
          If you or your organization have entered into a Master Service Agreement ("MSA") or other
          written agreement with Interlucent, that agreement governs your use of the Software and takes
          precedence over this EULA to the extent of any conflict. This EULA applies to all matters
          not addressed by your MSA.
        </p>

        <h2 style={h2Style}>1. License Grant</h2>
        <p>
          Subject to the terms of this EULA, we grant you a limited, non-exclusive, non-transferable,
          revocable license to use the Software solely in connection with the Interlucent platform and
          in accordance with your applicable agreement. This license does not include the right to sublicense,
          modify, or create derivative works of the Software, except as expressly permitted.
        </p>

        <h2 style={h2Style}>2. Restrictions</h2>
        <p>You may not:</p>
        <ul style={listStyle}>
          <li>Copy, modify, or distribute the Software or any part thereof;</li>
          <li>Reverse-engineer, decompile, disassemble, or otherwise attempt to derive the source code of the Software, except where expressly permitted by applicable law;</li>
          <li>Remove, alter, or obscure any proprietary notices, labels, or marks in the Software;</li>
          <li>Use the Software to build a competing product or service;</li>
          <li>Transfer, assign, or sublicense your rights under this EULA to any third party;</li>
          <li>Use the Software in any manner that violates applicable laws or regulations.</li>
        </ul>

        <h2 style={h2Style}>3. Ownership</h2>
        <p>
          The Software is licensed, not sold. Interlucent retains all right, title, and interest in and to
          the Software, including all intellectual property rights. This EULA does not convey any ownership
          interest in the Software. Your Content (as defined in the Terms of Use) remains yours.
        </p>

        <h2 style={h2Style}>4. Third-Party Components</h2>
        <p>
          The Software may include open-source or third-party components, each subject to its own license
          terms. A list of such components and their licenses is available upon request. Nothing in this
          EULA limits your rights under, or grants you rights that supersede, the terms of any applicable
          open-source license.
        </p>

        <h2 style={h2Style}>5. Updates and Modifications</h2>
        <p>
          We may release updates, patches, or new versions of the Software from time to time. Updates may
          be applied automatically. We are not obligated to provide updates, but if we do, they are governed
          by this EULA unless accompanied by separate terms.
        </p>

        <h2 style={h2Style}>6. Data Collection</h2>
        <p>
          The Software may collect usage telemetry, performance metrics, and diagnostic data to improve the
          Service. This data is collected in accordance with our{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>Privacy Policy</a>.
          We do not collect the contents of Your Content for any purpose other than providing the Service.
        </p>

        <h2 style={h2Style}>7. Disclaimer of Warranties</h2>
        <p>
          THE SOFTWARE IS PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
          IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, NON-INFRINGEMENT, OR UNINTERRUPTED AVAILABILITY. WE DO NOT WARRANT THAT THE SOFTWARE
          WILL BE ERROR-FREE OR THAT DEFECTS WILL BE CORRECTED.
        </p>

        <h2 style={h2Style}>8. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, INTERLUCENT SHALL NOT BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE
          OF THE SOFTWARE, INCLUDING BUT NOT LIMITED TO LOSS OF DATA, LOSS OF PROFITS, OR BUSINESS
          INTERRUPTION. OUR TOTAL LIABILITY UNDER THIS EULA SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNTS
          YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS
          (US$100).
        </p>

        <h2 style={h2Style}>9. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless Interlucent from any claims, damages, losses, or
          expenses (including reasonable legal fees) arising from your use of the Software in violation
          of this EULA or any applicable law.
        </p>

        <h2 style={h2Style}>10. Term and Termination</h2>
        <p>
          This EULA is effective until terminated. Your license terminates automatically if you breach any
          term of this EULA. We may also terminate this EULA at any time with notice. Upon termination,
          you must cease all use of the Software and destroy any copies in your possession. Sections 3,
          7, 8, 9, and 13 survive termination.
        </p>

        <h2 style={h2Style}>11. Export Compliance</h2>
        <p>
          You agree to comply with all applicable export control laws and regulations. You may not export
          or re-export the Software to any country, entity, or person prohibited by applicable law.
        </p>

        <h2 style={h2Style}>12. Changes to This EULA</h2>
        <p>
          We may update this EULA from time to time. Material changes will be communicated via the email
          associated with your account or through a notice in the Service. Continued use of the Software
          after the effective date constitutes acceptance of the updated EULA.
        </p>

        <h2 style={h2Style}>13. Governing Law</h2>
        <p>
          This EULA is governed by and construed in accordance with applicable law, without regard to
          conflict-of-law principles. Any disputes arising under this EULA shall be resolved in the courts
          of competent jurisdiction.
        </p>

        <h2 style={h2Style}>14. Contact</h2>
        <p>
          If you have questions about this EULA, please contact us at{' '}
          <a href="mailto:legal@interlucent.ai" style={linkStyle}>legal@interlucent.ai</a>.
        </p>
      </article>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#fafafa',
  display: 'flex',
  justifyContent: 'center',
  padding: '48px 24px',
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
};

const articleStyle: React.CSSProperties = {
  maxWidth: '720px',
  width: '100%',
  color: '#1a202c',
  fontSize: '15px',
  lineHeight: '1.7',
};

const h1Style: React.CSSProperties = {
  fontSize: '28px',
  fontWeight: 700,
  margin: '0 0 4px 0',
  letterSpacing: '-0.02em',
};

const metaStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#718096',
  margin: '0 0 32px 0',
};

const h2Style: React.CSSProperties = {
  fontSize: '17px',
  fontWeight: 600,
  margin: '28px 0 8px 0',
};

const listStyle: React.CSSProperties = {
  paddingLeft: '24px',
  margin: '8px 0',
};

const linkStyle: React.CSSProperties = {
  color: '#667eea',
  textDecoration: 'none',
};
